import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { toggleFavorite, type ModelConfig } from '@repancy/core';
import { testIds } from '../../lib/testIds';

interface ModelSelectorProps {
  selectedModelId: string | null;
  onSelect: (modelId: string) => void;
}

const PROVIDER_ORDER = [
  'Anthropic',
  'OpenAI',
  'Google',
  'Meta',
  'DeepSeek',
  'Mistral',
  'Qwen',
  'Cohere',
  'xAI',
];

function formatContextWindow(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return `${(n / 1_000).toFixed(0)}K`;
}

function formatPrice(n: number): string {
  return `$${n.toFixed(2)}`;
}

const dropdownBtnStyle: React.CSSProperties = {
  width: '100%',
  fontFamily: "var(--xp-font-body)",
  fontSize: 12,
  padding: '3px 6px',
  background: '#fff',
  color: '#000',
  borderStyle: 'solid',
  borderWidth: 2,
  borderTopColor: '#808080',
  borderLeftColor: '#808080',
  borderBottomColor: '#dfdfdf',
  borderRightColor: '#dfdfdf',
  cursor: 'pointer',
  textAlign: 'left',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxSizing: 'border-box' as const,
};

const listboxStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  maxHeight: 320,
  overflowY: 'auto',
  background: '#fff',
  borderStyle: 'solid',
  borderWidth: 2,
  borderTopColor: '#808080',
  borderLeftColor: '#808080',
  borderBottomColor: '#dfdfdf',
  borderRightColor: '#dfdfdf',
  zIndex: 9999,
  fontFamily: "var(--xp-font-body)",
  fontSize: 11,
};

const groupHeaderStyle: React.CSSProperties = {
  padding: '4px 6px',
  fontWeight: 'bold',
  fontSize: 11,
  background: '#ece9d8',
  color: 'var(--xp-selection)',
  borderBottom: '1px solid #808080',
  userSelect: 'none',
};

const itemStyle: React.CSSProperties = {
  padding: '3px 8px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

const itemHoverStyle: React.CSSProperties = {
  ...itemStyle,
  background: 'var(--xp-selection)',
  color: '#fff',
};

export function ModelSelector({ selectedModelId, onSelect }: ModelSelectorProps) {
  const dispatch = useAppDispatch();
  const models = useAppSelector((s) => s.models);
  const [open, setOpen] = useState(false);
  const [favOnly, setFavOnly] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const selectedModel = selectedModelId ? models.entities[selectedModelId] : null;

  const grouped = useMemo(() => {
    const allModels = models.ids
      .map((id) => models.entities[id])
      .filter((m): m is ModelConfig => !!m)
      .filter((m) => !favOnly || m.isFavorite);

    const groups: { provider: string; items: ModelConfig[] }[] = [];
    for (const provider of PROVIDER_ORDER) {
      const items = allModels.filter((m) => m.provider === provider);
      if (items.length > 0) groups.push({ provider, items });
    }
    return groups;
  }, [models, favOnly]);

  const handleToggleFav = useCallback(
    (e: React.MouseEvent, modelId: string) => {
      e.stopPropagation();
      dispatch(toggleFavorite(modelId));
    },
    [dispatch],
  );

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1 }}>
      <button
        style={dropdownBtnStyle}
        onClick={() => setOpen(!open)}
        data-testid={testIds.promptLab.modelSelector}
      >
        <span>
          {selectedModel
            ? `${selectedModel.name} (${selectedModel.provider})`
            : 'Select model...'}
        </span>
        <span style={{ fontSize: 10 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={listboxStyle} data-testid={testIds.promptLab.modelDropdown}>
          {/* Favorites toggle */}
          <div
            style={{
              padding: '4px 8px',
              borderBottom: '1px solid #ece9d8',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: '#dfdfdf',
            }}
          >
            <input
              type="checkbox"
              checked={favOnly}
              onChange={(e) => setFavOnly(e.target.checked)}
              id="fav-only-toggle"
              data-testid={testIds.promptLab.favoritesToggle}
            />
            <label
              htmlFor="fav-only-toggle"
              style={{ fontSize: 11, cursor: 'pointer' }}
            >
              Show favorites only
            </label>
          </div>

          {grouped.length === 0 && (
            <div style={{ padding: 12, color: '#808080', textAlign: 'center', fontStyle: 'italic' }}>
              No models match
            </div>
          )}

          {grouped.map((group) => (
            <div key={group.provider}>
              <div style={groupHeaderStyle} data-testid={testIds.promptLab.modelProviderGroup(group.provider)}>{group.provider}</div>
              {group.items.map((m) => (
                <div
                  key={m.id}
                  style={hoveredId === m.id ? itemHoverStyle : itemStyle}
                  onMouseEnter={() => setHoveredId(m.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => {
                    onSelect(m.id);
                    setOpen(false);
                  }}
                  data-testid={testIds.promptLab.modelOption(m.id)}
                >
                  <span
                    style={{
                      cursor: 'pointer',
                      fontSize: 14,
                      lineHeight: 1,
                      color: m.isFavorite ? '#FFD700' : hoveredId === m.id ? '#fff' : '#ece9d8',
                    }}
                    onClick={(e) => handleToggleFav(e, m.id)}
                    data-testid={testIds.promptLab.modelFavorite(m.id)}
                  >
                    {m.isFavorite ? '★' : '☆'}
                  </span>
                  <span style={{ flex: 1 }}>{m.name}</span>
                  <span
                    style={{
                      fontSize: 10,
                      color: hoveredId === m.id ? '#aaa' : '#808080',
                      marginLeft: 4,
                    }}
                  >
                    {formatContextWindow(m.contextWindow)} |{' '}
                    {formatPrice(m.pricing.prompt)}/{formatPrice(m.pricing.completion)}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
